/**
 * Conflict resolution modal shown when a queued mutation collides with a
 * server-side change made by someone else between the baseline snapshot and
 * the flush attempt. The user chooses: keep mine / use theirs / cancel.
 */
import { AlertTriangle } from 'lucide-react'
import { useOfflineSync } from '@/hooks/useOfflineSync'
import { Modal } from '@/components/ui/Modal'

function describeMutationType(type: string): string {
  if (type.startsWith('attendance')) return 'Présences'
  if (type.startsWith('grade')) return 'Notes'
  return type
}

export default function ConflictModal() {
  const { currentConflict, resolveKeepMine, resolveUseTheirs, dismissConflict } = useOfflineSync()

  if (!currentConflict) return null
  const m = currentConflict.mutation
  const server = currentConflict.serverState

  return (
    <Modal
      isOpen={!!currentConflict}
      onClose={dismissConflict}
      title="Modification conflictuelle"
      size="md"
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">Quelqu'un d'autre a modifié cette donnée pendant que vous étiez hors ligne.</p>
            <p className="text-xs opacity-80">
              Catégorie : {describeMutationType(m.type)} · Enregistrée {new Date(m.createdAt).toLocaleString('fr-FR')}
            </p>
          </div>
        </div>

        {server && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3">
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">Votre version (hors ligne)</p>
              <pre className="text-xs text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap break-words">
                {JSON.stringify(m.payload.rows?.[0] || m.payload, null, 2)}
              </pre>
            </div>
            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 p-3">
              <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1">Version actuelle (serveur)</p>
              <pre className="text-xs text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap break-words">
                {JSON.stringify(server, null, 2)}
              </pre>
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={dismissConflict}
            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            Plus tard
          </button>
          <button
            onClick={resolveUseTheirs}
            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            Utiliser la version serveur
          </button>
          <button
            onClick={resolveKeepMine}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            Garder la mienne
          </button>
        </div>
      </div>
    </Modal>
  )
}
